import { Room, RoomEvent, Track } from 'livekit-client';
import type { EventBus } from './event-bus';

export interface ConnectArgs {
  wsUrl: string;
  token: string;
  roomName: string;
  agentName: string;
  sessionId: string;
}

export interface RoomManager {
  connect(args: ConnectArgs): Promise<void>;
  /** `silent` skips the public sessionEnd event — for replacing a pre-warm room the host never got a session in. */
  disconnect(opts?: { silent?: boolean }): Promise<void>;
  getRoom(): Room | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _getRoomForTests(): any;
}

export function createRoomManager(opts: { eventBus: EventBus }): RoomManager {
  const { eventBus } = opts;
  let room: Room | null = null;
  let currentSessionId: string | null = null;
  let sessionEnded = false;
  let agentReadyEmitted = false;

  /**
   * Fire `agentReady` once the agent worker is actually present. Same
   * predicate `push-api.ts`'s `findAgent` uses, so the event can't claim the
   * agent is reachable while pushes still say 'no agent connected'.
   *
   * Called from three places because none alone is reliable: the agent may
   * already be here when we join, may join later, or may join before its
   * attributes land.
   */
  function maybeEmitAgentReady(r: Room, sessionId: string) {
    if (agentReadyEmitted) return;
    // `wireEvents` never detaches, so a previous session's Room can still
    // fire in here and latch the flag with a stale sessionId, suppressing the
    // live session's real event.
    if (r !== room) return;
    for (const p of r.remoteParticipants?.values() ?? []) {
      if (p.attributes?.['lk.agent.state']) {
        agentReadyEmitted = true;
        eventBus.emit('agentReady', { sessionId, identity: p.identity });
        return;
      }
    }
  }

  function emitSessionEnd(sessionId: string, reason: string) {
    if (sessionEnded) return;
    sessionEnded = true;
    eventBus.emit('sessionEnd', { sessionId, reason });
  }

  function wireEvents(r: Room, agentName: string, sessionId: string, roomName: string) {
    r.on(RoomEvent.ParticipantAttributesChanged, (attrs: Record<string, string>) => {
      if (attrs['lk.agent.state']) {
        eventBus.emit('agentStateChange', { state: attrs['lk.agent.state'] });
        maybeEmitAgentReady(r, sessionId);
      }
    });
    r.on(RoomEvent.ParticipantConnected, () => maybeEmitAgentReady(r, sessionId));
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    r.on(RoomEvent.TranscriptionReceived, (segments: any[], participant: any) => {
      // Normalize participant identity to 'user' or 'agent' so chat-panel
      // styling (right-aligned blue vs. left-aligned grey) doesn't depend
      // on whatever raw identity LiveKit assigns the agent worker
      // (which can be the agent name, `agent-<jobId>`, etc.).
      const localIdentity = r.localParticipant?.identity;
      const isLocal = participant?.identity === localIdentity;
      const normalized = isLocal ? 'user' : 'agent';
      for (const s of segments) {
        eventBus.emit('transcript', {
          id: s.id,
          text: s.text,
          participant: normalized,
          isFinal: !!s.final,
        });
      }
    });
    r.on(
      RoomEvent.DataReceived,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (payload: Uint8Array, _participant: any, _kind: any, topic?: string) => {
        if (topic !== 'tool-activity') return;
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          eventBus.emit('toolCall', data);
          // Citation piggybacks on tool-activity (see locked decision #2).
          if (data.tool === 'search_knowledge' && Array.isArray(data.data?.citations)) {
            for (const c of data.data.citations) eventBus.emit('citation', c);
          }
        } catch (e) {
          console.warn('[Mobeus] failed to parse tool-activity payload', e);
        }
      },
    );
    // Scenes arrive on their own topic, so this is a second DataReceived
    // registration rather than another branch in the one above — same split
    // hosted sites use (`voice-session-store.ts`), and it keeps the
    // tool-activity path byte-identical.
    //
    // Deliberately dumb: parse the envelope, emit it, let the renderer deal
    // with the DSL. Both publishers agree on `{type}` but disagree below it —
    // ONE_BRAIN (`agent/main.py`) always sends `dsl`, TWO_BRAIN
    // (`ui-engine/livekit_publisher.py`) may send legacy JSON scene fields
    // instead — and neither distinction belongs in the transport.
    r.on(
      RoomEvent.DataReceived,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (payload: Uint8Array, _participant: any, _kind: any, topic?: string) => {
        if (topic !== 'ui-engine:scene') return;
        try {
          const msg = JSON.parse(new TextDecoder().decode(payload));
          // `skeleton` (layout-only placeholder, published microseconds
          // before the real thing) and `scene` are the only two types either
          // publisher emits. Anything else is a protocol change we should
          // notice rather than silently render.
          if (msg?.type !== 'skeleton' && msg?.type !== 'scene') {
            console.warn('[Mobeus] unknown ui-engine:scene message type', msg?.type);
            return;
          }
          eventBus.emit('scene', msg);
        } catch (e) {
          console.warn('[Mobeus] failed to parse ui-engine:scene payload', e);
        }
      },
    );
    r.on(RoomEvent.Disconnected, () => {
      emitSessionEnd(sessionId, 'clean');
    });
    eventBus.emit('sessionStart', { sessionId, agentName, roomName });
  }

  return {
    async connect(args) {
      if (room && room.state !== 'disconnected') {
        console.warn('[Mobeus] already connected; disconnect first');
        return;
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      room = new (Room as any)({});
      currentSessionId = args.sessionId;
      sessionEnded = false;
      agentReadyEmitted = false;
      wireEvents(room as Room, args.agentName, args.sessionId, args.roomName);
      await (room as Room).connect(args.wsUrl, args.token);
      // Covers an agent already in the room, for which neither event fires.
      // Null-rechecked: `disconnect()` can land while this await settles.
      if (room) maybeEmitAgentReady(room, args.sessionId);
    },
    async disconnect(opts?: { silent?: boolean }) {
      if (!room) return;
      const sid = currentSessionId ?? '';
      await room.disconnect();
      if (!opts?.silent) emitSessionEnd(sid, 'clean');
      room = null;
      currentSessionId = null;
    },
    getRoom() {
      return room;
    },
    _getRoomForTests() {
      return room;
    },
  };
}

export { Track };
